<script setup lang="ts">
import AppInput from "@/components/UI/AppInput/AppInput.vue";
import AppSelect from "@/components/UI/AppSelect/AppSelect.vue";
import AppInputAmount from "@/components/UI/AppInputAmount/AppInputAmount.vue";
import AppDateTimePicker from "@/components/UI/AppDateTimePicker/AppDateTimePicker.vue";
import mtRadioGroup from "@/components/UI/mtRadio/mtRadioGroup.vue";
import MtButton from "@/components/UI/mtButton/index.vue";
import useToast from "@/components/UI/AppToast/useToast";
import AppAlert from "@/components/UI/AppAlert/AppAlert.vue";
import {FieldsType, rules, useValidate} from "@/services/validate.ts";
import {deepClone, validateEmail} from "@/services/utils";

interface Props {
  modelValue: Object;
  handler: (data) => Promise<any>;
  handlerButtonText?: string;
  disableButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  handlerButtonText: "Сохранить"
});
const emits = defineEmits(["update:modelValue", "submit"]);

const { validate, errors } = useValidate();

const fields = ref(modifyFields())

function modifyFields() {
  const fields = deepClone(props.modelValue);

  Object.values(fields)
      .forEach((field, index) => {
        const componentType = {
          string: {
            component: markRaw(AppInput)
          },
          integer: {
            component: markRaw(AppInputAmount)
          },
          date: {
            component: markRaw(AppDateTimePicker),
            bind: {
              format: "dd.MM.yyyy"
            }
          },
          list: {
            component: markRaw(AppSelect)
          },
          radio: {
            component: markRaw(mtRadioGroup),
            bind: {
              inline: true
            }
          }
        };
        const type = field.type ?? "string";
        const component = componentType[type];
        // при deepClone для props.modelValue параметр rules не скопировался
        if (field.key == 'email') {
          field.rules = {
            required: rules.required,
            email: rules.email
          }
        } else {
          field.rules = {};
        }
        field.component = component.component;
        field.bind = {
          ...field.bind,
          ...component.bind
        };

        // Обработка ошибок
        if(field.errors && field.errors.length){
          errors.value[field.key] = field.errors;
          delete field.errors;
        }
      });

  const groupedFields = Object.values(fields).reduce((acc, field) => {
    const groupUuid = field.groupUuid || "base";

    if (!acc[groupUuid]) {
      acc[groupUuid] = {
        uuid: groupUuid,
        name: field.groupName || "base",
        fields: []
      };
    }

    acc[groupUuid]["fields"].push(field);
    return acc;
  }, {});

  return Object.values(groupedFields);
}

const requestPending = ref(false);

async function submit() {
  let fieldsToValidate = fields.value.reduce((acc, field) => {
    Object.values(field?.fields ?? {}).forEach(field => {
      acc[field.key] = field;
    })
    return acc;
  }, []);

  await validate(fieldsToValidate as FieldsType);

  if (Object.keys(errors.value).length) {
    const { toast } = useToast();
    toast.error("Форма не прошла валидацию, пожалуйста, проверьте введенные данные");
    return;
  }

  requestPending.value = true;

  props.handler(fields.value)
      .finally(() => {
        requestPending.value = false;
      });

}

watch(() => props.modelValue, (newVal) => {
  fields.value = deepClone(newVal);
  fields.value = modifyFields();
}, {
  deep: true
});

async function onChange(val, key) {
  if (val != '') {
    if (key == 'email') {
      if (!validateEmail(val)) {
        errors.value[key] = [{$message: 'Введен неправильный формат e-mail'}];
      } else {
        delete errors.value[key];
      }
    } else {
      delete errors.value[key];
    }
  } else {
    errors.value[key] = [{$message: 'Поле не заполнено'}];
  }
}

</script>

<template>
  <div class="app-form-builder">
    <slot name="prepend-form" />
    <app-alert type="info" class="mb-4">Поля со звездочкой <sup>(*)</sup> обязательны для заполнения</app-alert>
    <div class="app-form-builder__groups">
      <div v-for="group in fields" class="app-form-builder__groups-group">
        <div v-if="group.uuid!=='base'" class="app-form-builder__groups-group-label">{{ group.name }}</div>
        <div class="app-form-builder__groups-group-fields">
          <template v-for="field in group['fields']">
            <component
                :is="field.component"
                v-bind="field.bind"
                v-model="field.modelValue"
                @change="(val)=> onChange(val, field.key)"
                :errors="errors[field.key]"
            />
          </template>
        </div>
      </div>

      <slot name="prepend-buttons" />
      <div class="app-form-builder__btn-wrapper">
        <mt-button
            @click="submit"
            :loading="requestPending"
            :disabled="props.disableButton"
            class="app-form-builder__btn"
            size="large"
        >
          {{ props.handlerButtonText }}
        </mt-button>
      </div>
      <slot name="append-buttons" />
    </div>
  </div>
</template>

<style lang="scss">
.app-form-builder {
  width: 100%;

  &__groups {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &-group {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      &-label {
        color: #7c869d;
      }

      &-fields {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
      }
    }
  }

  &__fields {
    //width: 100%;
    //display: flex;
    //flex-wrap: wrap;
  }

  &__btn-wrapper {
    margin-top: 20px;
    width: 100%;
  }
}
</style>